/**
 * Jeu d'icônes de Villopia — source unique (D§6).
 *
 * Dessinées pour le projet, dans le même registre que la ville : trait de 2 px,
 * extrémités arrondies, grille de 24, aucune fioriture. Pas d'emoji en production :
 * un emoji change de dessin selon la plateforme et casse l'unité de style.
 *
 * Le sprite est injecté une seule fois dans le document ; chaque usage n'est ensuite
 * qu'une référence `<use>`, donc quelques octets. Deux tailles canoniques :
 * `regular` (20 px) et `large` (26 px).
 */

export const ICON_NAMES = [
  'coin',
  'gem',
  'build',
  'city',
  'social',
  'trophy',
  'gear',
  'eye',
  'shield',
  'bolt',
  'star',
  'flower',
  'close',
  'check',
  'plus',
  'minus',
  'chevron',
  'clock',
  // Une icône = un sens (D§6). « Pivoter » portait un chevron, « déplacer » un plus,
  // « recentrer » un œil : trois gestes différents, trois signes qui mentaient.
  'rotate',
  'move',
  'focus',
  // Le livre des jalons (D§4 : « liste complète, progression, gemmes gagnées »).
  'book',
  // Les skins et thèmes de V2 — l'aperçu scellé de la boutique.
  'palette',
  // Le cinquième onglet du dock : « Mes jeux » (demande de Benoit, 18 août 2026).
  'gamepad',
  // Le nettoyage du bitume orphelin (ROADMAP 5.2). Une icône de PLUS plutôt qu'une
  // détournée : « moins » dit déjà « dézoomer », et deux sens pour un signe est
  // exactement ce que le D§6 interdit.
  'road',
  // Un pictogramme par bâtiment. Quatre cartes portant la même maison rendent la
  // boutique illisible : le joueur choisit à la lecture au lieu du coup d'œil.
  'bHouse',
  'bCottage',
  'bStand',
  'bGrocery',
  'bFish',
  'bRestaurant',
  'bDoctor',
  'bCinema',
  'bSchool',
  'bOffice',
  // Les quatre points de repère en gemmes (ROADMAP 2.3). Un pictogramme chacun, pour la
  // même raison que les bâtiments : à 20 px, la silhouette est tout ce qui se lit.
  'lmTower',
  'lmPlay',
  'lmPool',
  'lmPad',
] as const;

export type IconName = (typeof ICON_NAMES)[number];

const STROKE = 'fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"';

/** Corps de chaque symbole, dans une grille de 24 × 24. */
const PATHS: Record<IconName, string> = {
  coin: `<circle cx="12" cy="12" r="9" fill="currentColor" opacity=".22"/>
    <circle cx="12" cy="12" r="9" ${STROKE} stroke-width="2"/>
    <circle cx="12" cy="12" r="4.6" ${STROKE} stroke-width="1.8"/>
    <path d="M12 3.2v2M12 18.8v2M3.2 12h2M18.8 12h2" ${STROKE} stroke-width="1.8"/>`,

  road: `<path d="M8.5 3h7l2.5 18H6z" fill="currentColor" opacity=".22"/>
    <path d="M8.5 3h7l2.5 18H6z" ${STROKE} stroke-width="2"/>
    <path d="M12 5.5v3M12 11v3M12 16.5v3" ${STROKE} stroke-width="1.8"/>`,

  gem: `<path d="M6 3h12l4 6-10 12L2 9z" fill="currentColor" opacity=".25"/>
    <path d="M6 3h12l4 6-10 12L2 9z" ${STROKE} stroke-width="2"/>
    <path d="M2 9h20M9 3 7 9l5 12M15 3l2 6-5 12" ${STROKE} stroke-width="1.5"/>`,

  build: `<path d="M4 20V10l8-6 8 6v10" ${STROKE} stroke-width="2"/>
    <path d="M9 20v-5h6v5" ${STROKE} stroke-width="2"/>
    <path d="M2 20h20" ${STROKE} stroke-width="2"/>`,

  city: `<path d="M3 21V9l5-3v4l5-3v5l6-2v11z" ${STROKE} stroke-width="2"/>
    <path d="M2 21h20" ${STROKE} stroke-width="2"/>`,

  social: `<circle cx="18" cy="5.5" r="2.8" ${STROKE} stroke-width="2"/>
    <circle cx="6" cy="12" r="2.8" ${STROKE} stroke-width="2"/>
    <circle cx="18" cy="18.5" r="2.8" ${STROKE} stroke-width="2"/>
    <path d="m8.5 10.6 7-3.6M8.5 13.4l7 3.6" ${STROKE} stroke-width="2"/>`,

  trophy: `<path d="M7 4h10v5a5 5 0 0 1-10 0z" ${STROKE} stroke-width="2"/>
    <path d="M7 5.5H4.5A2.5 2.5 0 0 0 7 10M17 5.5h2.5A2.5 2.5 0 0 1 17 10M12 14v3M8.5 20h7" ${STROKE} stroke-width="2"/>`,

  gear: `<circle cx="12" cy="12" r="3.2" ${STROKE} stroke-width="2"/>
    <path d="M12 2.6l1.5 2.6 3-.4.6 3 2.6 1.5-1.4 2.7 1.4 2.7-2.6 1.5-.6 3-3-.4L12 21.4l-1.5-2.6-3 .4-.6-3-2.6-1.5 1.4-2.7-1.4-2.7 2.6-1.5.6-3 3 .4z" ${STROKE} stroke-width="1.7"/>`,

  eye: `<path d="M2 12s3.8-6 10-6 10 6 10 6-3.8 6-10 6-10-6-10-6z" ${STROKE} stroke-width="2"/>
    <circle cx="12" cy="12" r="2.8" fill="currentColor"/>`,

  shield: `<path d="M12 2.5 20 6v6c0 5-3.4 8.2-8 9.5-4.6-1.3-8-4.5-8-9.5V6z" fill="currentColor" opacity=".22"/>
    <path d="M12 2.5 20 6v6c0 5-3.4 8.2-8 9.5-4.6-1.3-8-4.5-8-9.5V6z" ${STROKE} stroke-width="2"/>`,

  bolt: `<path d="M13 2 4 13.5h6L11 22l9-11.5h-6z" fill="currentColor"/>`,

  star: `<path d="m12 2.8 2.9 5.9 6.5.9-4.7 4.6 1.1 6.5-5.8-3-5.8 3 1.1-6.5L2.6 9.6l6.5-.9z" fill="currentColor"/>`,

  flower: `<circle cx="12" cy="7" r="3" fill="currentColor" opacity=".55"/>
    <circle cx="7" cy="12" r="3" fill="currentColor" opacity=".55"/>
    <circle cx="17" cy="12" r="3" fill="currentColor" opacity=".55"/>
    <circle cx="12" cy="12" r="2.4" fill="currentColor"/>
    <path d="M12 15v6" ${STROKE} stroke-width="2"/>`,

  close: `<path d="m6 6 12 12M18 6 6 18" ${STROKE} stroke-width="2.4"/>`,

  check: `<path d="m4.5 12.5 5 5 10-11" ${STROKE} stroke-width="2.6"/>`,

  plus: `<path d="M12 5v14M5 12h14" ${STROKE} stroke-width="2.4"/>`,

  minus: `<path d="M5 12h14" ${STROKE} stroke-width="2.4"/>`,

  chevron: `<path d="m9 5 7 7-7 7" ${STROKE} stroke-width="2.4"/>`,

  rotate: `<path d="M20 12a8 8 0 1 1-2.34-5.66" ${STROKE} stroke-width="2.2"/>
    <path d="M20 3v4.5h-4.5" ${STROKE} stroke-width="2.2"/>`,

  move: `<path d="M12 3v18M3 12h18" ${STROKE} stroke-width="2"/>
    <path d="m9 6 3-3 3 3M9 18l3 3 3-3M6 9 3 12l3 3M18 9l3 3-3 3" ${STROKE} stroke-width="2"/>`,

  focus: `<circle cx="12" cy="12" r="3.4" ${STROKE} stroke-width="2"/>
    <path d="M12 3v3.4M12 17.6V21M3 12h3.4M17.6 12H21" ${STROKE} stroke-width="2.2"/>`,

  book: `<path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15.5H6.5A2.5 2.5 0 0 0 4 21z" ${STROKE} stroke-width="2"/>
    <path d="M4 18.5A2.5 2.5 0 0 1 6.5 16H20M8 7.5h8M8 11h5" ${STROKE} stroke-width="2"/>`,

  palette: `<path d="M12 3a9 9 0 1 0 0 18c1.4 0 2-.9 2-1.8 0-.8-.5-1.2-.5-2 0-1 .8-1.7 1.9-1.7H17a4 4 0 0 0 4-4c0-4.7-4-8.5-9-8.5z" ${STROKE} stroke-width="2"/>
    <circle cx="7.6" cy="10.2" r="1.2" fill="currentColor"/>
    <circle cx="11.2" cy="7.4" r="1.2" fill="currentColor"/>
    <circle cx="15.4" cy="8.6" r="1.2" fill="currentColor"/>`,

  gamepad: `<rect x="2.5" y="7.5" width="19" height="10" rx="5" ${STROKE} stroke-width="2"/>
    <path d="M8 10.5v4M6 12.5h4" ${STROKE} stroke-width="1.8"/>
    <circle cx="16" cy="11" r="1.1" fill="currentColor"/>
    <circle cx="18.4" cy="13.6" r="1.1" fill="currentColor"/>`,
  clock: `<circle cx="12" cy="12" r="9" ${STROKE} stroke-width="2"/>
    <path d="M12 7v5.4l3.4 2" ${STROKE} stroke-width="2"/>`,

  /* ── Bâtiments ──────────────────────────────────────────────────────────
     Même trait, même grille, même densité que le reste du jeu. Chacun se
     reconnaît à sa SILHOUETTE, pas à un détail : à 20 px, un détail disparaît. */

  bHouse: `<path d="M4 20V10l8-6 8 6v10z" ${STROKE} stroke-width="2"/>
    <path d="M9.5 20v-5.5h5V20" ${STROKE} stroke-width="2"/>`,

  // Cheminée + corps bas et large : sans ça, la chaumière et la maison partageaient la
  // même silhouette de toit pointu, et deux cartes voisines devenaient indiscernables
  // à 20 px — exactement le défaut qu'un pictogramme par bâtiment devait corriger.
  bCottage: `<path d="M4 20v-7h16v7z" ${STROKE} stroke-width="2"/>
    <path d="M2 13 12 6l10 7" ${STROKE} stroke-width="2"/>
    <path d="M16.6 8.4V5h2.6v5.3" ${STROKE} stroke-width="2"/>
    <path d="M9 20v-4.5h3V20M14.5 15.5h2.5" ${STROKE} stroke-width="2"/>`,

  // Château d'eau : cuve large sur quatre pieds écartés. La cuve est en HAUT et le vide
  // en bas — c'est ce vide qui distingue la silhouette de celle d'un bâtiment.
  lmTower: `<path d="M6 4h12v6H6z" ${STROKE} stroke-width="2"/>
    <path d="M8 10 6.5 20M16 10l1.5 10M12 10v10M8.5 15h7" ${STROKE} stroke-width="2"/>`,

  // Aire de jeux : un toboggan. La diagonale est le seul trait qui se lise à 20 px, et
  // aucune autre icône du jeu n'en porte.
  lmPlay: `<path d="M6 20v-9h5V6" ${STROKE} stroke-width="2"/>
    <path d="M11 6h5v5l-7 9" ${STROKE} stroke-width="2"/>
    <path d="M4 20h16" ${STROKE} stroke-width="2"/>`,

  // Piscine : le bassin vu de face, et la ligne d'eau ondulée qui dit « eau » sans
  // couleur — l'icône est monochrome.
  lmPool: `<path d="M4 9h16v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" ${STROKE} stroke-width="2"/>
    <path d="M4 14c2 0 2 1.6 4 1.6s2-1.6 4-1.6 2 1.6 4 1.6 2-1.6 4-1.6" ${STROKE} stroke-width="2"/>`,

  // Héliport : le H dans son cercle. Le signe international, donc zéro apprentissage.
  lmPad: `<circle cx="12" cy="12" r="8" ${STROKE} stroke-width="2"/>
    <path d="M9.5 8v8M14.5 8v8M9.5 12h5" ${STROKE} stroke-width="2"/>`,

  bStand: `<path d="M4 10h16l-1.5 10h-13z" ${STROKE} stroke-width="2"/>
    <path d="M3 10l2-4h14l2 4M8 10c0 4 8 4 8 0" ${STROKE} stroke-width="2"/>`,

  bGrocery: `<path d="M4 9h16v11H4z" ${STROKE} stroke-width="2"/>
    <path d="M3 9l1.6-4h14.8L21 9M8 20v-6h8v6" ${STROKE} stroke-width="2"/>`,

  bFish: `<path d="M3 12c4-5 10-5 13 0-3 5-9 5-13 0z" ${STROKE} stroke-width="2"/>
    <path d="M16 12l5-3.5v7z" ${STROKE} stroke-width="2"/>
    <circle cx="7.5" cy="11" r="1.1" fill="currentColor"/>`,

  bRestaurant: `<path d="M7 3v8a2.5 2.5 0 0 0 5 0V3" ${STROKE} stroke-width="2"/>
    <path d="M9.5 3v8M9.5 13.5V21" ${STROKE} stroke-width="2"/>
    <path d="M17 3c-1.6 1.6-2 4-2 6.5 0 1.4.7 2 2 2V21" ${STROKE} stroke-width="2"/>`,

  bDoctor: `<path d="M4 9h16v11H4z" ${STROKE} stroke-width="2"/>
    <path d="M3 9l1.6-4h14.8L21 9" ${STROKE} stroke-width="2"/>
    <path d="M12 11.5v5M9.5 14h5" ${STROKE} stroke-width="2.2"/>`,

  bCinema: `<path d="M3 8h18v12H3z" ${STROKE} stroke-width="2"/>
    <path d="M3 8l3.5-4h11L21 8M8 8l3.5-4M14 8l3.5-4" ${STROKE} stroke-width="2"/>
    <path d="M10 12.5v5l4.5-2.5z" ${STROKE} stroke-width="2"/>`,

  bSchool: `<path d="M4 20V10l8-4 8 4v10z" ${STROKE} stroke-width="2"/>
    <path d="M12 6V2.5M12 2.5l4 1.5-4 1.5" ${STROKE} stroke-width="2"/>
    <path d="M9.5 20v-5h5v5M2 20h20" ${STROKE} stroke-width="2"/>`,

  bOffice: `<path d="M5 21V4h9v17z" ${STROKE} stroke-width="2"/>
    <path d="M14 10h5v11h-5" ${STROKE} stroke-width="2"/>
    <path d="M8 8h3M8 12h3M8 16h3M16.5 14h.01M16.5 17.5h.01" ${STROKE} stroke-width="2"/>`,
};

const SPRITE_ID = 'villopia-icon-sprite';

/** Balisage du sprite complet. Exporté pour permettre un rendu côté serveur ou en test. */
export function spriteMarkup(): string {
  const symbols = ICON_NAMES.map(
    (name) => `<symbol id="vi-${name}" viewBox="0 0 24 24">${PATHS[name]}</symbol>`,
  ).join('');

  return `<svg id="${SPRITE_ID}" width="0" height="0" aria-hidden="true" style="position:absolute"><defs>${symbols}</defs></svg>`;
}

/** Injecte le sprite une seule fois. Appels suivants sans effet. */
export function installIconSprite(doc: Document = document): void {
  if (doc.getElementById(SPRITE_ID) !== null) {
    return;
  }
  const host = doc.createElement('div');
  host.innerHTML = spriteMarkup();
  const sprite = host.firstElementChild;
  if (sprite !== null) {
    doc.body.prepend(sprite);
  }
}

export type IconSize = 'regular' | 'large';

export interface IconOptions {
  readonly size?: IconSize;
  /** Couleur CSS ; par défaut l'icône hérite de la couleur du texte. */
  readonly color?: string;
  /**
   * Texte alternatif. Omis, l'icône est décorative et masquée aux lecteurs d'écran —
   * ce qui est le cas correct quand un libellé voisin dit déjà la même chose.
   */
  readonly label?: string;
}

/** Crée un élément `<svg>` référençant un symbole du sprite. */
export function icon(name: IconName, options: IconOptions = {}): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.classList.add('vi', `vi--${options.size ?? 'regular'}`);

  if (options.color !== undefined) {
    svg.style.color = options.color;
  }

  if (options.label === undefined) {
    svg.setAttribute('aria-hidden', 'true');
  } else {
    svg.setAttribute('role', 'img');
    svg.setAttribute('aria-label', options.label);
  }

  const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
  use.setAttribute('href', `#vi-${name}`);
  svg.append(use);

  return svg;
}
